import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {number | null} */
  #animationOnlyRemoveLine = null;

  /** @type {number[] | null} */
  #batchRemoveLines = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("click", this.#handleRemoveButtonClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("click", this.#handleRemoveButtonClick);
    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  /**
   * Logs every cart remove button click.
   * @param {MouseEvent} event
   */
  #handleRemoveButtonClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const removeButton = target.closest(".cart-items__remove");
    if (
      !(removeButton instanceof HTMLButtonElement) ||
      !this.contains(removeButton)
    )
      return;

    console.log("[cart-items] remove button clicked", {
      button: removeButton,
      ariaLabel: removeButton.getAttribute("aria-label"),
      action: removeButton.getAttribute("on:click"),
    });

    if (removeButton.hasAttribute("data-cart-donation-remove")) return;
    if (this.refs.cartItemRows.length !== 2) return;

    const donationRemoveButton = this.querySelector(
      "[data-cart-donation-remove]",
    );
    if (!(donationRemoveButton instanceof HTMLButtonElement)) return;

    const regularLine = this.#getLineFromRemoveButton(removeButton);
    const donationLine = this.#getLineFromRemoveButton(donationRemoveButton);
    if (!regularLine || !donationLine) return;

    this.#animationOnlyRemoveLine = donationLine;
    this.#batchRemoveLines = [regularLine, donationLine];
    donationRemoveButton.click();
  };

  /**
   * @param {HTMLButtonElement} button
   */
  #getLineFromRemoveButton(button) {
    const action = button.getAttribute("on:click") || "";
    const match = action.match(/\/onLineItemRemove\/(\d+)/);
    return match ? Number(match[1]) : null;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const animationOnly = this.#animationOnlyRemoveLine === Number(line);
    if (animationOnly) this.#animationOnlyRemoveLine = null;

    const batchRemoveLines = this.#batchRemoveLines?.includes(Number(line))
      ? this.#batchRemoveLines
      : null;
    if (batchRemoveLines) this.#batchRemoveLines = null;

    const runCartUpdate = () => {
      if (animationOnly) return;

      this.updateQuantity({
        line,
        quantity: 0,
        action: "clear",
        batchRemoveLines,
      });
    };

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) {
      runCartUpdate();
      return;
    }

    const remove = () => {
      cartItemRowToRemove.remove();
      runCartUpdate();
    };

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty(
      "--row-height",
      `${cartItemRowToRemove.clientHeight}px`,
    );
    cartItemRowToRemove.classList.add("removing");

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    void this.#runQuantityUpdate(config);
  }

  /**
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   * @param {number[] | null} [config.batchRemoveLines]
   */
  async #runQuantityUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const sectionsCsv = Array.from(sectionsToUpdate).join(",");

    cartTotal?.shimmer();

    try {
      const batchRemoveLines = Array.isArray(config.batchRemoveLines)
        ? config.batchRemoveLines
        : null;
      const body = batchRemoveLines
        ? await this.#buildBatchRemoveBody(batchRemoveLines, sectionsCsv)
        : JSON.stringify({
            line: line,
            quantity: quantity,
            sections: sectionsCsv,
            sections_url: window.location.pathname,
          });

      const response = await fetch(
        batchRemoveLines
          ? Theme.routes.cart_update_url
          : `${Theme.routes.cart_change_url}`,
        fetchConfig("json", { body }),
      );
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      this.#handleQuantityResponse(parsedResponseText, line);
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * @param {number[]} lines
   * @param {string} sectionsCsv
   */
  async #buildBatchRemoveBody(lines, sectionsCsv) {
    const cartResponse = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    const cart = await cartResponse.json();
    const updates = {};

    lines.forEach((line) => {
      const item = cart.items?.[line - 1];
      if (item?.key) updates[item.key] = 0;
    });

    return JSON.stringify({
      updates,
      sections: sectionsCsv,
      sections_url: window.location.pathname,
    });
  }

  /**
   * @param {Record<string, unknown>} parsedResponseText
   * @param {number} line
   */
  #handleQuantityResponse(parsedResponseText, line) {
    resetShimmer(this);

    if (parsedResponseText.errors) {
      this.#handleCartError(line, parsedResponseText);
      return;
    }

    const sectionHtml = parsedResponseText.sections?.[this.sectionId];
    if (!sectionHtml || typeof sectionHtml !== "string") return;

    const newSectionHTML = new DOMParser().parseFromString(
      sectionHtml,
      "text/html",
    );

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount = newSectionHTML.querySelector(
      '[ref="cartItemCount"]',
    )?.textContent;
    const newCartItemCount = newCartHiddenItemCount
      ? parseInt(newCartHiddenItemCount, 10)
      : 0;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: newCartItemCount,
        source: "cart-items-component",
        sections: /** @type {Record<string, string>} */ (
          parsedResponseText.sections
        ),
      }),
    );

    morphSection(this.sectionId, sectionHtml);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml).catch((error) => {
        console.error(error);
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      });
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
